import React, { useState, useEffect, useRef } from "react";
import { useApp } from "../context/AppContext";

interface Suggestion { label: string; lat: number; lon: number }

interface Props {
  city: string;
  initiallySelected?: boolean;
  onSelect: (city: string, lat: number, lon: number) => void;
  onChangeText?: (text: string) => void;
  placeholder?: string;
  inputStyle?: React.CSSProperties;
  hintText?: string;
}

export default function CitySearchInput({ city, initiallySelected, onSelect, onChangeText, placeholder, inputStyle, hintText }: Props) {
  const { lang } = useApp();
  const [inputValue, setInputValue] = useState(city);
  const [selected, setSelected] = useState(!!initiallySelected);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [show, setShow] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => { setInputValue(city); setSelected(!!initiallySelected); }, [city, initiallySelected]);

  useEffect(() => {
    if (selected) return;
    const fetchCities = async () => {
      if (inputValue.length < 2) { setSuggestions([]); return; }
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(inputValue)}&format=json&limit=5&featuretype=city&accept-language=${lang || "en"}`);
        const data = await res.json();
        // Nom + pays plutôt que les 3 premiers segments de l'adresse OpenStreetMap : hors de
        // France, ces segments intermédiaires sont souvent un quartier/une délégation/un
        // gouvernorat ("Nabeul, Al Hadaek, Délégation Nabeul") au lieu d'une région lisible
        // ("Paris, Île-de-France") — un utilisateur ne reconnaissait pas sa propre ville dans ce
        // résultat et abandonnait l'inscription en pensant que la recherche ne fonctionnait pas.
        // Le dernier segment de display_name est quasi toujours le pays, quelle que soit la
        // profondeur de la hiérarchie administrative locale.
        const seen = new Set<string>();
        const s = data
          .map((item: any) => {
            const parts = item.display_name.split(", ");
            const label = parts.length > 1 ? `${parts[0]}, ${parts[parts.length - 1]}` : parts[0];
            return { label, lat: parseFloat(item.lat), lon: parseFloat(item.lon) };
          })
          .filter((x: Suggestion) => (seen.has(x.label) ? false : (seen.add(x.label), true)));
        setSuggestions(s);
        setShow(s.length > 0);
      } catch { setSuggestions([]); }
    };
    const t = setTimeout(fetchCities, 300);
    return () => clearTimeout(t);
  }, [inputValue, selected]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setShow(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const defaultStyle: React.CSSProperties = {
    width: "100%", padding: "10px 40px 10px 14px", borderRadius: 10,
    border: `1.5px solid ${selected ? "#0a0a0a" : "#E0E0E0"}`, fontSize: 14, outline: "none", boxSizing: "border-box",
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <input
        type="text"
        value={inputValue}
        onChange={e => { setInputValue(e.target.value); setSelected(false); setShow(true); onChangeText?.(e.target.value); }}
        onFocus={() => !selected && suggestions.length > 0 && setShow(true)}
        placeholder={placeholder || "Paris, London, New York..."}
        autoComplete="off"
        style={{ ...defaultStyle, ...inputStyle }}
      />
      {selected && <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: "#0a0a0a", fontWeight: 700, fontSize: 15 }}>✓</span>}
      {show && suggestions.length > 0 && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100,
          backgroundColor: "white", borderRadius: 12, marginTop: 4,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)", border: "1px solid #E0E0E0", overflow: "hidden",
        }}>
          {suggestions.map((s, i) => (
            <div
              key={i}
              onClick={() => { setInputValue(s.label); setSelected(true); setShow(false); onSelect(s.label, s.lat, s.lon); }}
              style={{ padding: "12px 16px", cursor: "pointer", fontSize: 14, borderBottom: i < suggestions.length - 1 ? "1px solid #F5F5F5" : "none" }}
              onMouseOver={e => (e.currentTarget.style.backgroundColor = "#F5F5F5")}
              onMouseOut={e => (e.currentTarget.style.backgroundColor = "white")}
            >
              {s.label}
            </div>
          ))}
        </div>
      )}
      {inputValue && !selected && (
        <div style={{ fontSize: 12, color: "#999", marginTop: 6 }}>{hintText || "Sélectionne ta ville dans la liste pour continuer"}</div>
      )}
    </div>
  );
}
